import React, { useState, useEffect, useCallback } from "react";

// 🌸 Green Shakti Challenge - Mobile-first Daily Eco-Habit Tracker
// Track small sustainable actions, earn Eco Stars and Shakti Coins

const STORAGE_KEY_WALLET = "sheearns_shakti_wallet_v1";
const STORAGE_KEY_GREEN = "sheearns_green_shakti_v1";
const STORAGE_KEY_LAST_RESET = "sheearns_last_reset_v1";
const STORAGE_KEY_COMPLETED = "sheearns_completed_days_v1";
const STORAGE_KEY_AWARDED = "sheearns_awarded_milestones_v1";
const STORAGE_KEY_REMINDERS_ENABLED = "sheearns_reminders_enabled_v1";
const STORAGE_KEY_LAST_REMINDER = "sheearns_last_reminder_v1";
const STORAGE_KEY_REMINDER_HOUR = "sheearns_reminder_hour_v1";
const STORAGE_KEY_REMINDER_MINUTE = "sheearns_reminder_minute_v1";

const DEFAULT_ACTIONS = [
  { id: 1, text: "Used reusable water bottle" },
  { id: 2, text: "Planted a tree or plant" },
  { id: 3, text: "Recycled household waste" },
  { id: 4, text: "Avoided single-use plastics" },
  { id: 5, text: "Saved electricity / water" },
];

const MILESTONES = [
  { days: 7, reward: 20 },
  { days: 14, reward: 30 },
  { days: 30, reward: 50 },
];

export default function GreenShaktiChallenge() {
  const [wallet, setWallet] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WALLET);
      return raw ? JSON.parse(raw) : { coins: 0, totalXP: 0, peacePoints: 0 };
    } catch {
      return { coins: 0, totalXP: 0, peacePoints: 0 };
    }
  });

  const [actions, setActions] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_GREEN);
      return raw ? JSON.parse(raw) : DEFAULT_ACTIONS.map(a => ({ ...a, done: false }));
    } catch {
      return DEFAULT_ACTIONS.map(a => ({ ...a, done: false }));
    }
  });

  const [lastResetStr, setLastResetStr] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_LAST_RESET);
      return raw || null;
    } catch {
      return null;
    }
  });

  const [completedDays, setCompletedDays] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_COMPLETED);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [awardedMilestones, setAwardedMilestones] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_AWARDED);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [remindersEnabled, setRemindersEnabled] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_REMINDERS_ENABLED);
      return raw ? JSON.parse(raw) : false;
    } catch {
      return false;
    }
  });

  const [reminderHour, setReminderHour] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_REMINDER_HOUR);
      return raw ? parseInt(raw) : 8;
    } catch {
      return 8;
    }
  });

  const [reminderMinute, setReminderMinute] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_REMINDER_MINUTE);
      return raw ? parseInt(raw) : 0;
    } catch {
      return 0;
    }
  });

  const [currentStreak, setCurrentStreak] = useState(0);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationText, setNotificationText] = useState("");

  const audioContextRef = React.useRef(null);

  const createAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  const playSound = (frequency = 440, duration = 0.2, type = 'sine') => {
    const audioContext = createAudioContext();
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  };

  const playToggleSound = () => {
    playSound(523.25, 0.1, 'square'); // C5 note, short beep
  };

  const playSuccessSound = () => {
    // Ascending arpeggio
    const notes = [392, 440, 493.88, 523.25]; // G4, A4, B4, C5
    notes.forEach((freq, index) => {
      setTimeout(() => playSound(freq, 0.15, 'sine'), index * 150);
    });
  };

  const getCurrentStreak = useCallback((completed, todayStr) => {
    let streak = 0;
    let date = new Date(todayStr);
    while (streak < 1000) {
      const dateStr = date.toISOString().split('T')[0];
      if (completed.includes(dateStr)) {
        streak++;
        date.setDate(date.getDate() - 1);
      } else {
        break;
      }
    }
    return streak;
  }, []);

  // Notification permission and daily reminder logic
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        // Request permission if enabled
        if (remindersEnabled) {
          Notification.requestPermission();
        }
      }
    }
  }, [remindersEnabled]);

  useEffect(() => {
    const checkDailyReminder = () => {
      if (!remindersEnabled || Notification.permission !== 'granted') return;

      const todayStr = new Date().toISOString().split('T')[0];
      const lastReminderStr = localStorage.getItem(STORAGE_KEY_LAST_REMINDER);
      const now = new Date();
      const reminderTimeToday = new Date(now);
      reminderTimeToday.setHours(reminderHour, reminderMinute, 0, 0);

      // Show reminder if now is after reminder time today, not completed today, and no reminder today
      if (now >= reminderTimeToday && !completedDays.includes(todayStr) && lastReminderStr !== todayStr) {
        const reminder = new Notification('🌱 Green Shakti Reminder', {
          body: 'Time to complete your daily eco-actions and keep your streak alive! 🌸',
          icon: '/favicon.ico', // Assume favicon exists
          tag: 'green-shakti-reminder',
        });
        localStorage.setItem(STORAGE_KEY_LAST_REMINDER, todayStr);
      }
    };

    checkDailyReminder();
  }, [remindersEnabled, completedDays, reminderHour, reminderMinute]);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (!lastResetStr || lastResetStr < todayStr) {
      // Auto-reset for new day
      const resetActions = DEFAULT_ACTIONS.map(a => ({ ...a, done: false }));
      const updatedWallet = { ...wallet, coins: 0 };
      setActions(resetActions);
      setWallet(updatedWallet);
      setLastResetStr(todayStr);
      localStorage.setItem(STORAGE_KEY_GREEN, JSON.stringify(resetActions));
      localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(updatedWallet));
      localStorage.setItem(STORAGE_KEY_LAST_RESET, todayStr);
    }
  }, []);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    const streak = getCurrentStreak(completedDays, todayStr);
    setCurrentStreak(streak);
  }, [completedDays, getCurrentStreak]);

  const showMilestoneNotification = (newMs) => {
    const msTexts = newMs.map(m => `${m.days}-day streak badge +${m.reward} XP`).join(", ");
    setNotificationText(`🎉 Congratulations! Unlocked: ${msTexts}`);
    setShowNotification(true);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  const toggleAction = (id) => {
    playToggleSound();

    const prevAction = actions.find(a => a.id === id);
    const prevDone = prevAction ? prevAction.done : false;
    const increment = prevDone ? -2 : 2;
    const newDailyCoins = Math.max(0, wallet.coins + increment);
    let newTotalXP = wallet.totalXP;
    if (increment > 0) {
      newTotalXP += increment;
    }
    let updatedWallet = { ...wallet, coins: newDailyCoins, totalXP: newTotalXP };

    const updated = actions.map(a => a.id === id ? { ...a, done: !a.done } : a);
    setActions(updated);

    const todayStr = new Date().toISOString().split('T')[0];
    const completedCount = updated.filter(a => a.done).length;
    const wasCompleted = completedDays.includes(todayStr);
    const isCompleted = completedCount === DEFAULT_ACTIONS.length;
    let newCompleted = [...completedDays];
    if (wasCompleted && !isCompleted) {
      newCompleted = newCompleted.filter(d => d !== todayStr);
    } else if (!wasCompleted && isCompleted) {
      newCompleted.push(todayStr);
      playSuccessSound(); // Play success sound when completing the day
    }
    const completedChanged = newCompleted.length !== completedDays.length;
    if (completedChanged) {
      setCompletedDays(newCompleted);
      localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(newCompleted));
    }

    // Check for milestone rewards only when completing the day
    if (!wasCompleted && isCompleted) {
      const streak = getCurrentStreak(newCompleted, todayStr);
      const newMs = MILESTONES.filter(m => streak >= m.days && !awardedMilestones.includes(`${m.days}`));
      if (newMs.length > 0) {
        const extra = newMs.reduce((sum, m) => sum + m.reward, 0);
        updatedWallet = { ...updatedWallet, totalXP: updatedWallet.totalXP + extra };
        const newAwarded = [...awardedMilestones, ...newMs.map(m => `${m.days}`)];
        setAwardedMilestones(newAwarded);
        localStorage.setItem(STORAGE_KEY_AWARDED, JSON.stringify(newAwarded));
        showMilestoneNotification(newMs);
      }
    }

    setWallet(updatedWallet);
    localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(updatedWallet));
    localStorage.setItem(STORAGE_KEY_GREEN, JSON.stringify(updated));
  };

  const resetDay = () => {
    const resetActions = actions.map(a => ({ ...a, done: false }));
    const updatedWallet = { ...wallet, coins: 0 };
    setActions(resetActions);
    setWallet(updatedWallet);

    const todayStr = new Date().toISOString().split('T')[0];
    const wasCompleted = completedDays.includes(todayStr);
    if (wasCompleted) {
      const newCompleted = completedDays.filter(d => d !== todayStr);
      setCompletedDays(newCompleted);
      localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(newCompleted));
    }

    localStorage.setItem(STORAGE_KEY_GREEN, JSON.stringify(resetActions));
    localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(updatedWallet));
  };

  const toggleReminders = () => {
    const newEnabled = !remindersEnabled;
    setRemindersEnabled(newEnabled);
    localStorage.setItem(STORAGE_KEY_REMINDERS_ENABLED, JSON.stringify(newEnabled));
    if (newEnabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const updateReminderHour = (e) => {
    const newHour = parseInt(e.target.value);
    setReminderHour(newHour);
    localStorage.setItem(STORAGE_KEY_REMINDER_HOUR, newHour.toString());
  };

  const updateReminderMinute = (e) => {
    const newMinute = parseInt(e.target.value);
    setReminderMinute(newMinute);
    localStorage.setItem(STORAGE_KEY_REMINDER_MINUTE, newMinute.toString());
  };

  return (
    <div className="min-h-screen bg-gray-800 p-4 sm:p-6 flex flex-col items-center relative">
      <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-200 mb-2 text-center">🌱 Green Shakti Challenge</h1>
      <p className="text-sm sm:text-base text-white mb-4 sm:mb-6 text-center max-w-md">Track your eco-friendly actions and earn Eco Stars + Shakti Coins!</p>

      <div className="bg-black backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg w-full max-w-sm sm:max-w-md md:max-w-lg text-center ">
        <div className="space-y-3">
          {actions.map(a => (
            <button 
              key={a.id} 
              onClick={() => toggleAction(a.id)} 
              className={`w-full p-3 sm:p-4 rounded-xl border transition-all duration-200 ${
                a.done 
                  ? 'bg-rose-100 line-through text-rose-50 border-rose-300' 
                  : 'border-rose-200 hover:bg-rose-500 hover:border-rose-300 hover:shadow-sm'
              }`}
            >
              <span className="text-sm sm:text-base">{a.text}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 text-sm sm:text-base text-rose-50 text-center font-medium">
          Actions completed: <span className="text-rose-100">{actions.filter(a => a.done).length}</span> / {actions.length}
        </div>
        <div className="mt-2 text-sm sm:text-base text-rose-50 text-center font-semibold">
          Coins earned today: <span className="text-rose-200">{wallet.coins}</span>
        </div>
        <div className="mt-2 text-sm sm:text-base text-rose-50 text-center font-semibold">
          Total XP: <span className="text-rose-200">{wallet.totalXP}</span> (includes milestones)
        </div>
        <div className="mt-2 text-sm sm:text-base text-rose-50 text-center font-semibold">
          Streak: <span className="text-rose-200">{currentStreak}</span> days 🔥
        </div>
        <div className="mt-4 text-rose-200 text-center font-medium text-xs sm:text-sm">Milestones</div>
        <div className="mt-2 flex justify-center space-x-2 flex-wrap gap-2">
          {MILESTONES.map(m => (
            <div
              key={m.days}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all shadow-sm ${
                awardedMilestones.includes(`${m.days}`)
                  ? 'bg-rose-500 text-white shadow-md'
                  : 'bg-rose-50 text-rose-400 border border-rose-200'
              }`}
            >
              {awardedMilestones.includes(`${m.days}`) ? `🌟 ${m.days}d +${m.reward}` : `${m.days}d`}
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-center">
          <button 
            onClick={resetDay} 
            className="px-6 py-2 sm:px-8 sm:py-3 rounded-xl border-2 border-rose-300 bg-rose-50 text-rose-700 text-sm sm:text-base font-medium hover:bg-rose-100 hover:border-rose-400 transition-all duration-200 shadow-sm"
          >
            Reset Day
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-rose-200">
          <label className="flex items-center justify-center space-x-2 cursor-pointer text-sm text-rose-700 mb-2 block">
            <input
              type="checkbox"
              checked={remindersEnabled}
              onChange={toggleReminders}
              className="rounded border-rose-300 text-rose-600 focus:ring-rose-500 mr-2"
            />
            <span>Enable daily reminders</span>
          </label>
          {remindersEnabled && (
            <div className="text-center text-xs text-rose-600">
              <div className="flex justify-center items-center space-x-1">
                <span>Time:</span>
                <select
                  value={reminderHour}
                  onChange={updateReminderHour}
                  className="border border-rose-300 rounded px-2 py-1 text-xs bg-white"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {i.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={reminderMinute}
                  onChange={updateReminderMinute}
                  className="border border-rose-300 rounded px-2 py-1 text-xs bg-white"
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i}>
                      {i.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-1 text-xs opacity-75">Notifications shown after this time if tasks incomplete</div>
            </div>
          )}
        </div>
      </div>

      {showNotification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 w-11/12 max-w-sm bg-rose-500 text-white p-4 rounded-xl shadow-xl z-50 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center space-x-2">
            <span className="text-lg">🎉</span>
            <span className="text-sm font-semibold">{notificationText}</span>
          </div>
        </div>
      )}

      <footer className="mt-8 sm:mt-12 text-xs sm:text-sm text-white text-center max-w-md">
        Part of SheEarns • Progress saved locally 🌸
      </footer>
    </div>
  );
}